/**
 * useViewerKeyboard — the viewer's global keyboard shortcuts.
 *
 * One window keydown listener:
 *  - `D` (no modifier, not while typing) → toggle the dev layout-debug overlay.
 *  - Cmd/Ctrl+Z → undo placement; Cmd/Ctrl+Shift+Z or Cmd/Ctrl+Y → redo.
 *
 * Owns the `debugLayout` state it toggles and returns it, so the composition
 * passes it to the board. Undo/redo come from the placements service.
 * Extracted verbatim from App.tsx.
 */
import {
  useEffect,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";

export interface ViewerKeyboardInput {
  readonly undo: () => void;
  readonly redo: () => void;
}

export interface ViewerKeyboard {
  readonly debugLayout: boolean;
  readonly setDebugLayout: Dispatch<SetStateAction<boolean>>;
}

export function useViewerKeyboard({
  undo,
  redo,
}: ViewerKeyboardInput): ViewerKeyboard {
  const [debugLayout, setDebugLayout] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Debug-layout toggle — no modifier, just `D`. Ignore when typing in an
      // input so it doesn't swallow user text.
      const active = document.activeElement as HTMLElement | null;
      const activeTag = active?.tagName;
      // Also treat shadow-root hosts as "in field" — e.g. the Operator devtools
      // injects a shadow root whose inner inputs aren't visible as activeElement.
      const inField =
        activeTag === "INPUT" ||
        activeTag === "TEXTAREA" ||
        active?.isContentEditable ||
        !!active?.shadowRoot;
      if (
        !inField &&
        (e.key === "d" || e.key === "D") &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey
      ) {
        e.preventDefault();
        setDebugLayout((v) => !v);
        return;
      }
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      if (e.key.toLowerCase() === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if (
        (e.key.toLowerCase() === "z" && e.shiftKey) ||
        e.key.toLowerCase() === "y"
      ) {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo]);

  return { debugLayout, setDebugLayout };
}
